import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ costCenterId: '', postingDate: today(), amount: '', description: '', reference: '' });

export function PostCostModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const costCenters = useQuery({ queryKey: ['finance', 'cost-centers'], queryFn: () => financeApi.listCostCenters({ activeOnly: true }).then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.postCost(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'controlling'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.costCenterId || !form.postingDate || form.amount === '' || Number(form.amount) <= 0) { setError(t('co.error.postRequired')); return; }
    save.mutate({
      costCenterId: form.costCenterId,
      postingDate: form.postingDate,
      amount: Number(form.amount),
      description: form.description || null,
      reference: form.reference || null,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('co.post.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('co.field.costCenter')}</span>
          <select className="input" value={form.costCenterId} onChange={set('costCenterId')} required>
            <option value="">-</option>
            {(costCenters.data || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('co.field.date')}</span>
            <input className="input" type="date" value={form.postingDate} onChange={set('postingDate')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('co.field.amount')}</span>
            <input className="input num" type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('co.field.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} />
        </label>
        <label className="field">
          <span className="field-label">{t('co.field.reference')}</span>
          <input className="input" value={form.reference} onChange={set('reference')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
